"use client";

import {
  getAuthErrorMessage,
  useAuthUser,
  useCheckPhoneMutation,
  useForgotPasswordMutation,
  useLoginWithCodeMutation,
  useLoginWithPasswordMutation,
  useResendSignupCodeMutation,
  useResetPasswordMutation,
  useSendLoginCodeMutation,
  useSignupMutation,
  useVerifySignupMutation,
} from "@/hooks/auth.hook";
import {
  CodeFormData,
  PasswordFormData,
  PhoneFormData,
  ResetPasswordFormData,
  SignupFormData,
  Step,
} from "@/types/auth-types";
import { PASSWORD_PATTERN } from "@/utils/auth-utils";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

/**
 * All login/signup/reset flow logic — step machine, forms, mutations and
 * handlers — extracted from the UI so <LoginModal /> stays a thin, presentational
 * shell. Pass the modal's open state so the flow can reset & auto-close.
 */
export function useLoginFlow({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const t = useTranslations("auth");

  const { data: currentUser } = useAuthUser();

  // ── Mutations ──────────────────────────────────────────────────────────────
  const checkPhoneMutation = useCheckPhoneMutation();
  const signupMutation = useSignupMutation();
  const verifySignupMutation = useVerifySignupMutation();
  const resendSignupMutation = useResendSignupCodeMutation();
  const sendLoginCodeMutation = useSendLoginCodeMutation();
  const loginWithPasswordMutation = useLoginWithPasswordMutation();
  const loginWithCodeMutation = useLoginWithCodeMutation();
  const forgotPasswordMutation = useForgotPasswordMutation();
  const resetPasswordMutation = useResetPasswordMutation();

  // ── State ────────────────────────────────────────────────────────────────
  const [step, setStep] = useState<Step>("phone");
  const [activePhone, setActivePhone] = useState("");
  const [activeEmail, setActiveEmail] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  // The signup code also went out by SMS to the phone (4jawaly).
  const [codeBySms, setCodeBySms] = useState(false);
  // The login code: SMS (Saudi number) or email.
  const [loginChannel, setLoginChannel] = useState<"sms" | "email">("email");
  const [countdown, setCountdown] = useState(0);

  // ── Forms ────────────────────────────────────────────────────────────────
  const phoneForm = useForm<PhoneFormData>({ defaultValues: { phone: "" } });
  const passwordForm = useForm<PasswordFormData>({
    defaultValues: { password: "" },
  });
  const signupForm = useForm<SignupFormData>({
    defaultValues: {
      username: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
    },
  });
  const verifySignupForm = useForm<CodeFormData>({
    defaultValues: { code: "" },
  });
  const loginCodeForm = useForm<CodeFormData>({ defaultValues: { code: "" } });
  const resetPasswordForm = useForm<ResetPasswordFormData>({
    defaultValues: { code: "", password: "", confirmPassword: "" },
  });

  const resetState = useCallback(() => {
    phoneForm.reset({ phone: "" });
    passwordForm.reset({ password: "" });
    signupForm.reset({
      username: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
    });
    verifySignupForm.reset({ code: "" });
    loginCodeForm.reset({ code: "" });
    resetPasswordForm.reset({ code: "", password: "", confirmPassword: "" });
    setStep("phone");
    setActivePhone("");
    setActiveEmail("");
    setMaskedEmail("");
    setCountdown(0);
  }, [
    loginCodeForm,
    passwordForm,
    phoneForm,
    resetPasswordForm,
    signupForm,
    verifySignupForm,
  ]);

  const startCountdown = (seconds = 30) => setCountdown(seconds);

  // ── Effects: reset on close, lock scroll, countdown tick, auto-close ──────
  useEffect(() => {
    if (!isOpen) {
      const frame = window.requestAnimationFrame(resetState);
      return () => window.cancelAnimationFrame(frame);
    }
  }, [isOpen, resetState]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!countdown) return;
    const timer = window.setInterval(() => {
      setCountdown((c) => (c > 0 ? c - 1 : 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [countdown]);

  useEffect(() => {
    if (isOpen && currentUser) onClose();
  }, [currentUser, isOpen, onClose]);

  const handleApiError = (error: unknown) => toast.error(getAuthErrorMessage(error));

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handlePhoneSubmit = phoneForm.handleSubmit(async (values) => {
    try {
      const result = await checkPhoneMutation.mutateAsync({ phone: values.phone });
      setActivePhone(values.phone);

      if (result.status === "existing_user") {
        setMaskedEmail(result.emailMasked || "");
        setLoginChannel(result.codeChannel === "sms" ? "sms" : "email");
        setActiveEmail(result.email || "");
        // Saudi number → the SMS code goes out right away (no options screen).
        // If it can't, the options (email code / password) show instead.
        if (result.codeChannel === "sms" && (await handleSendLoginCode(values.phone))) return;
        setStep("method");
        return;
      }
      if (result.status === "pending_signup") {
        setMaskedEmail(result.emailMasked || "");
        setActiveEmail(result.email || "");
        setStep("verifySignup");
        startCountdown();
        return;
      }
      signupForm.reset({
        username: "",
        email: "",
        phone: values.phone,
        password: "",
        confirmPassword: "",
      });
      setStep("signup");
    } catch (error) {
      handleApiError(error);
    }
  });

  const handleSignupSubmit = signupForm.handleSubmit(async (values) => {
    try {
      const response = await signupMutation.mutateAsync(values);
      setActivePhone(values.phone);
      setActiveEmail(response.data.email);
      setMaskedEmail(values.email);
      setCodeBySms(!!response.data.sms);
      setStep("verifySignup");
      startCountdown(60); // the server allows one SMS a minute per number
      toast.success(t("accountCreated"));
    } catch (error) {
      handleApiError(error);
    }
  });

  const handleVerifySignupSubmit = verifySignupForm.handleSubmit(async (values) => {
    try {
      await verifySignupMutation.mutateAsync({ email: activeEmail || undefined, phone: activePhone || undefined, code: values.code });
      toast.success(t("verifiedSuccess"));
      onClose();
    } catch (error) {
      handleApiError(error);
    }
  });

  const handleSendLoginCode = async (phone: string = activePhone) => {
    try {
      const response = await sendLoginCodeMutation.mutateAsync({ phone });
      const sms = response.data.channel === "sms";
      setMaskedEmail(response.data.emailMasked);
      setLoginChannel(sms ? "sms" : "email");
      loginCodeForm.reset({ code: "" });
      setStep("loginCode");
      startCountdown(sms ? 60 : undefined); // one SMS a minute per number
      return true;
    } catch (error) {
      handleApiError(error);
      setLoginChannel("email");
      return false;
    }
  };

  const handleLoginWithPassword = passwordForm.handleSubmit(async (values) => {
    try {
      await loginWithPasswordMutation.mutateAsync({
        phone: activePhone,
        password: values.password,
      });
      toast.success(t("loginSuccess"));
      onClose();
    } catch (error) {
      handleApiError(error);
    }
  });

  const handleLoginWithCode = loginCodeForm.handleSubmit(async (values) => {
    try {
      await loginWithCodeMutation.mutateAsync({ phone: activePhone, code: values.code });
      toast.success(t("loginSuccess"));
      onClose();
    } catch (error) {
      handleApiError(error);
    }
  });

  const handleForgotPassword = async () => {
    try {
      const response = await forgotPasswordMutation.mutateAsync({ phone: activePhone });
      setMaskedEmail(response.data.emailMasked);
      resetPasswordForm.reset({ code: "", password: "", confirmPassword: "" });
      setStep("resetPassword");
      startCountdown(response.data.retryAfter || 30);
      toast.success(t("resetCodeSent"));
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleResetPassword = resetPasswordForm.handleSubmit(async (values) => {
    try {
      await resetPasswordMutation.mutateAsync({
        phone: activePhone,
        code: values.code,
        password: values.password,
        confirmPassword: values.confirmPassword,
      });
      toast.success(t("passwordResetSuccess"));
      onClose();
    } catch (error) {
      handleApiError(error);
    }
  });

  const resendCurrentCode = async () => {
    try {
      if (step === "verifySignup") {
        const r = await resendSignupMutation.mutateAsync({ email: activeEmail || undefined, phone: activePhone || undefined });
        setCodeBySms(!!r.data?.sms);
        startCountdown(60);
        toast.success(t("resend"));
        return;
      }
      if (step === "loginCode") await sendLoginCodeMutation.mutateAsync({ phone: activePhone });
      if (step === "resetPassword") await forgotPasswordMutation.mutateAsync({ phone: activePhone });
      startCountdown(step === "loginCode" && loginChannel === "sms" ? 60 : undefined);
      toast.success(t("resend"));
    } catch (error) {
      handleApiError(error);
    }
  };

  // Go one step back in the flow.
  const goBack = () => {
    if (step === "method") setStep("phone");
    // A Saudi number never saw the options screen: back goes where it came from.
    else if (step === "loginCode") setStep(loginChannel === "sms" ? "phone" : "method");
    else if (step === "password") setStep(loginChannel === "sms" ? "loginCode" : "method");
    else if (step === "signup") setStep("phone");
    else if (step === "verifySignup") setStep(activePhone ? "signup" : "phone");
    else if (step === "resetPassword") setStep("password");
  };

  const formattedCountdown = `00:${String(countdown).padStart(2, "0")}`;

  const passwordRules = {
    required: t("passwordRequired"),
    pattern: { value: PASSWORD_PATTERN, message: t("passwordStrength") },
  };

  return {
    // state
    step,
    setStep,
    activePhone,
    activeEmail,
    maskedEmail,
    codeBySms,
    loginChannel,
    countdown,
    formattedCountdown,
    // forms
    phoneForm,
    passwordForm,
    signupForm,
    verifySignupForm,
    loginCodeForm,
    resetPasswordForm,
    passwordRules,
    // handlers
    handlePhoneSubmit,
    handleSignupSubmit,
    handleVerifySignupSubmit,
    handleSendLoginCode,
    handleLoginWithPassword,
    handleLoginWithCode,
    handleForgotPassword,
    handleResetPassword,
    resendCurrentCode,
    goBack,
    // mutations (for pending states)
    checkPhoneMutation,
    signupMutation,
    verifySignupMutation,
    resendSignupMutation,
    sendLoginCodeMutation,
    loginWithPasswordMutation,
    loginWithCodeMutation,
    forgotPasswordMutation,
    resetPasswordMutation,
  };
}
